import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { canAccessAdmin, canAccessTeam } from '../../lib/roles'

interface NavItem {
  label: string
  to: string
  icon: string
}

interface Props {
  open: boolean
  onClose: () => void
}

export function Sidebar({ open, onClose }: Props) {
  const { profile, switchOrg } = useAuth()
  const navigate = useNavigate()
  const role = profile?.role ?? 'viewer'
  const memberships = profile?.memberships ?? []
  const hasMultipleOrgs = memberships.length > 1

  const items: NavItem[] = [
    { label: 'Dashboard', to: '/', icon: '\u229E' },
  ]

  if (canAccessAdmin(role)) {
    items.push({ label: 'Admin', to: '/admin', icon: '\u2699' })
  }

  if (canAccessTeam(role)) {
    items.push({ label: 'Team', to: '/team', icon: '\u2295' })
    items.push({ label: 'Audit Log', to: '/audit-log', icon: '\u2637' })
  }

  if (profile?.mi_enabled || canAccessAdmin(role)) {
    items.push({ label: 'Analytics', to: '/analytics', icon: '\u2261' })
  }
  items.push({ label: 'Settings', to: '/settings', icon: '\u2630' })

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={onClose}
        />
      )}

      <aside className={`fixed top-14 left-0 bottom-0 w-60 bg-white border-r border-gray-200 overflow-y-auto z-40 transition-transform md:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      } md:block`}>
        <nav className="py-4">
          {profile?.org_name && (
            <div className="px-4 pb-3 mb-3 border-b border-gray-100">
              <p className="text-[10px] uppercase tracking-wide text-brand font-semibold">Organization</p>
              {hasMultipleOrgs ? (
                <select
                  value={profile.org_id ?? ''}
                  onChange={(e) => { switchOrg(e.target.value); navigate('/') }}
                  className="w-full mt-1 text-sm text-gray-900 font-medium bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 cursor-pointer"
                >
                  {memberships.map(m => (
                    <option key={m.org_id} value={m.org_id}>
                      {m.org_name}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-gray-900 truncate font-medium">{profile.org_name}</p>
              )}
            </div>
          )}

          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-brand/5 text-brand font-medium border-r-2 border-brand'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <span className="text-base">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  )
}
